import { HardDrive, Cpu, Circuitry, Stack, Desktop, Hammer, Info, WifiSlash, X, Warning } from '@phosphor-icons/react';
import type { ProcessedFarm } from '../../hooks/useFarms.js';
import { hsUnitToHps } from '../../hooks/useFarms.js';
import { formatHashrate, formatUptime, formatTimeAgo, tempColor } from '../../utils/format.js';
import type { HiveStats, GpuInfo } from '../../services/api.js';

interface FarmDetailsProps {
  farm: ProcessedFarm;
  coreTempThreshold: number;
  memoryTempThreshold: number;
  onClose: () => void;
}

export function FarmDetails({ farm, coreTempThreshold, memoryTempThreshold, onClose }: FarmDetailsProps) {
  return (
    <div class="farm-details data-card">
      <div class="details-header data-card-header">
        <div class="details-title-group">
          <HardDrive size={16} />
          <span>Детали фермы {farm.name}</span>
        </div>
        <button class="btn-icon" onClick={onClose} style={{ width: '28px', height: '28px' }}>
          <X size={14} />
        </button>
      </div>

      {farm.isOnline ? (
        <div class="data-card-body">
          <GpuTable farm={farm} coreTempThreshold={coreTempThreshold} memoryTempThreshold={memoryTempThreshold} />
          <SystemInfo farm={farm} />
        </div>
      ) : (
        <div class="offline-status">
          <WifiSlash size={24} weight="duotone" />
          <div>
            <strong>Ферма не в сети</strong>
            <p style={{ marginTop: '4px', fontSize: 'var(--text-sm)', color: 'var(--tx-secondary)' }}>
              Последнее обновление: {farm.lastModified ? formatTimeAgo(farm.lastModified) : 'неизвестно'}
            </p>
          </div>
        </div>
      )}
    </div>
  );
}

function TempBar({ value, warnAt, criticalAt }: { value: number | null; warnAt: number; criticalAt: number }) {
  if (value == null) return <span class="temp-bar-text" style={{ color: 'var(--tx-muted)' }}>N/A</span>;
  const pct = Math.min(100, Math.max(0, (value / 110) * 100));
  const color = tempColor(value, warnAt, criticalAt);
  const isCritical = value >= criticalAt;

  return (
    <div class="temp-bar-wrap">
      <div class="temp-bar">
        <div
          class="temp-bar-fill"
          style={{
            width: `${pct}%`,
            background: color,
            animation: isCritical ? 'blink-alert 1s ease-in-out infinite' : undefined,
          }}
        />
      </div>
      <span class="temp-bar-text" style={{ color }}>{value}&deg;C</span>
    </div>
  );
}

function GpuTable({ farm, coreTempThreshold, memoryTempThreshold }: {
  farm: ProcessedFarm;
  coreTempThreshold: number;
  memoryTempThreshold: number;
}) {
  const stats = farm.raw.stats;
  if (!stats) return null;

  // Full gpu list (incl. filtered-out iGPU) so rig-level arrays stay positional.
  const busMapping = createBusMapping(stats, farm.raw.hello.params.gpu);

  return (
    <>
      <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)', marginBottom: 'var(--space-3)' }}>
        <span style={{ color: 'var(--cyan)', display: 'flex' }}><Cpu size={14} /></span>
        <strong style={{ fontSize: 'var(--text-sm)' }}>GPU устройства</strong>
      </div>
      <div class="table-container" style={{ marginBottom: 'var(--space-6)' }}>
        <table class="data-table">
          <thead>
            <tr>
              <th>#</th>
              <th>Наименование</th>
              <th>Хэшрейт</th>
              <th>Ядро</th>
              <th>Память</th>
              <th>FAN</th>
              <th>Мощность</th>
            </tr>
          </thead>
          <tbody>
            {farm.gpus.map((gpu, idx) => (
              <GpuRow
                key={idx}
                idx={idx}
                gpu={gpu}
                busMapping={busMapping}
                coreTempThreshold={coreTempThreshold}
                memoryTempThreshold={memoryTempThreshold}
              />
            ))}
          </tbody>
        </table>
      </div>
    </>
  );
}

function GpuRow({ idx, gpu, busMapping, coreTempThreshold, memoryTempThreshold }: {
  idx: number;
  gpu: GpuInfo;
  busMapping: Record<string, GpuStatEntry>;
  coreTempThreshold: number;
  memoryTempThreshold: number;
}) {
  // Everything is resolved per-GPU by busid — works no matter which miner
  // (AMD/Nvidia) owns this card. Hashrate is already normalized to H/s.
  const gpuStats = busMapping[gpu.busid] ?? {};

  const hsValue = Number(gpuStats.hashrate) || 0;
  const hr = formatHashrate(hsValue);
  const isZeroHashrate = hsValue === 0;

  const coreTemp = typeof gpuStats.temp === 'number' ? gpuStats.temp : null;
  const memTemp = typeof gpuStats.mtemp === 'number' ? gpuStats.mtemp : null;
  const fanSpeed = typeof gpuStats.fan === 'number' ? gpuStats.fan : null;
  const power = typeof gpuStats.power === 'number' ? gpuStats.power : null;
  const memSize = Math.ceil(parseInt(gpu.mem) / 1024);
  const gpuName = `${gpu.name} ${memSize}GB ${gpu.mem_type}`;

  // Flag zero fan as potential issue
  const zeroFan = fanSpeed != null && fanSpeed === 0;

  return (
    <tr>
      <td>{idx}</td>
      <td>
        <div style={{ display: 'flex', alignItems: 'center', gap: 'var(--space-2)' }}>
          <span
            class={`gpu-indicator ${gpu.brand}`}
            style={{ width: '10px', height: '10px', flexShrink: 0 }}
          />
          <span style={{ color: gpu.brand === 'nvidia' ? 'var(--nvidia)' : gpu.brand === 'amd' ? 'var(--amd)' : undefined }}>
            {gpuName}
          </span>
          {isZeroHashrate && (
            <span style={{ color: 'var(--red)', flexShrink: 0, display: 'flex' }} title="Нет хэшрейта"><Warning size={12} weight="fill" /></span>
          )}
        </div>
      </td>
      <td>
        <span style={{ fontFamily: 'var(--font-mono)', color: isZeroHashrate ? 'var(--red)' : undefined }}>
          {hr.value} {hr.unit}
        </span>
      </td>
      <td>
        <TempBar value={coreTemp} warnAt={coreTempThreshold - 10} criticalAt={coreTempThreshold} />
      </td>
      <td>
        <TempBar value={memTemp} warnAt={memoryTempThreshold - 10} criticalAt={memoryTempThreshold} />
      </td>
      <td>
        <span style={{ color: zeroFan ? 'var(--amber)' : undefined }}>
          {fanSpeed != null ? `${fanSpeed}%` : 'N/A'}
          {zeroFan && ' ⚠'}
        </span>
      </td>
      <td>{power != null ? `${power}W` : 'N/A'}</td>
    </tr>
  );
}

interface GpuStatEntry {
  temp?: number;
  mtemp?: number;
  fan?: number;
  power?: number;
  hashrate?: number; // normalized to H/s
}

/**
 * Map each GPU (by PCI busid) to its stats. Rig-level arrays (temp/mtemp/fan/
 * power) are positional to the FULL gpu list; hashrate comes from whichever
 * miner owns that GPU's bus — a rig can run two miners at once (AMD + Nvidia),
 * so we walk BOTH miner blocks and normalize each by its own hs_units.
 */
function createBusMapping(stats: HiveStats, gpus: GpuInfo[]): Record<string, GpuStatEntry> {
  const mapping: Record<string, GpuStatEntry> = {};
  const p = stats.params;

  gpus.forEach((gpu, i) => {
    mapping[gpu.busid] = {
      temp: p.temp?.[i],
      mtemp: p.mtemp?.[i],
      fan: p.fan?.[i],
      power: p.power?.[i],
    };
  });

  for (const ms of [p.miner_stats, p.miner_stats2]) {
    if (!ms?.bus_numbers) continue;
    const factor = hsUnitToHps(ms.hs_units);
    ms.bus_numbers.forEach((busNum, idx) => {
      const busid = `0${busNum.toString(16)}:00.0`;
      const entry = mapping[busid] ?? (mapping[busid] = {});
      entry.hashrate = (Number(ms.hs?.[idx]) || 0) * factor;
      // Fall back to the miner's own per-GPU temp/fan if the rig didn't report one.
      if (entry.temp == null) entry.temp = ms.temp?.[idx];
      if (entry.fan == null) entry.fan = ms.fan?.[idx];
    });
  }

  return mapping;
}

function SystemInfo({ farm }: { farm: ProcessedFarm }) {
  const hello = farm.raw.hello.params;
  const stats = farm.raw.stats?.params;
  const minerLine = (name?: string, ms?: { ver?: string; algo?: string; uptime?: number }) =>
    `${name ?? 'Unknown'} v${ms?.ver ?? ''}, ${ms?.algo ?? ''}, uptime: ${formatUptime(ms?.uptime ?? 0)}`;

  const items = [
    { icon: Circuitry, label: 'Мат. плата', value: `${hello.mb.manufacturer} ${hello.mb.product}` },
    { icon: Cpu, label: 'Процессор', value: hello.cpu.model },
    { icon: Stack, label: 'Ядра', value: String(hello.cpu.cores) },
    { icon: HardDrive, label: 'Накопитель', value: hello.disk_model },
    { icon: Desktop, label: 'NVIDIA', value: hello.nvidia_version || '—' },
    { icon: Desktop, label: 'AMD', value: hello.amd_version || '—' },
    { icon: Hammer, label: 'Майнер', value: minerLine(stats?.miner, stats?.miner_stats) },
    ...(stats?.miner2
      ? [{ icon: Hammer, label: 'Майнер 2', value: minerLine(stats.miner2, stats.miner_stats2) }]
      : []),
  ];

  return (
    <>
      <div class="sysinfo-header">
        <Info size={14} />
        <strong>Системная информация</strong>
      </div>
      <table class="sysinfo-table">
        <tbody>
          {items.map(({ icon: Icon, label, value }) => (
            <tr key={label}>
              <td class="sysinfo-icon-cell"><Icon size={14} /></td>
              <td class="sysinfo-label">{label}</td>
              <td class="sysinfo-value">{value}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </>
  );
}
